import React, { useRef, useState, useEffect } from "react";
import {
  CollapsibleWrapper,
  CollapsibleHeader,
  CollapsibleContent,
  CollapsibleTitle,
  TitleWrapper,
  CollapsibleSubtitle,
  CollapsibleCount,
  IconWrapper,
  DeleteButton,
  HeaderIcon,
} from "./styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { CardVariant } from "../../utils/colorVariants";

// Re-export for backward compatibility
export type { CardVariant };

interface KLCollapsibleSectionProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete?: () => void;
  secondary?: boolean;
  count?: number;
  children: React.ReactNode;
  /**
   * Icon to display next to the title
   */
  icon?: React.ReactNode;
  /**
   * Visual variant for the card
   */
  variant?: CardVariant;
}

const KLCollapsibleSection: React.FC<KLCollapsibleSectionProps> = ({
  title,
  subtitle,
  isExpanded,
  onToggle,
  onDelete,
  secondary,
  count,
  children,
  icon,
  variant = "default",
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | "none">(0);

  useEffect(() => {
    if (isExpanded && contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setMaxHeight((prevHeight) => {
        if (prevHeight !== contentHeight) {
          return contentHeight;
        }
        return prevHeight;
      });
    } else {
      setMaxHeight((prevHeight) => {
        if (prevHeight !== 0) {
          return 0;
        }
        return prevHeight;
      });
    }
  }, [isExpanded]);

  const handleTransitionEnd = () => {
    if (isExpanded) {
      setMaxHeight("none");
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (isExpanded && contentRef.current) {
        const newHeight = contentRef.current.scrollHeight;
        setMaxHeight((current) => {
          if (current !== "none" && current !== newHeight) {
            return newHeight;
          } else if (current === "none") {
            // If currently "none", set to height then back to "none" after transition
            setTimeout(() => {
              setMaxHeight("none");
            }, 300);
            return newHeight;
          }
          return current;
        });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded || !contentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === contentRef.current) {
          const newHeight = entry.target.scrollHeight;
          setMaxHeight((current) => {
            // Only update if not "none" and height actually changed
            if (current !== "none" && current !== newHeight) {
              return newHeight;
            }
            return current;
          });
        }
      }
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isExpanded]);

  return (
    <CollapsibleWrapper>
      <CollapsibleHeader
        onClick={onToggle}
        $isCollapsed={!isExpanded}
        $secondary={secondary}
        $variant={variant}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        {icon && <HeaderIcon $variant={variant}>{icon}</HeaderIcon>}
        <CollapsibleTitle>
          <TitleWrapper>
            {title}
            {count && <CollapsibleCount>({count})</CollapsibleCount>}
          </TitleWrapper>
          {subtitle && <CollapsibleSubtitle>{subtitle}</CollapsibleSubtitle>}
        </CollapsibleTitle>
        <IconWrapper>
          {onDelete && (
            <DeleteButton
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              מחק
            </DeleteButton>
          )}
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconWrapper>
      </CollapsibleHeader>
      <CollapsibleContent
        ref={contentRef}
        $maxHeight={maxHeight}
        $isExpanded={isExpanded}
        onTransitionEnd={handleTransitionEnd}
      >
        {children}
      </CollapsibleContent>
    </CollapsibleWrapper>
  );
};

export default React.memo(KLCollapsibleSection);
